import { createElement } from 'react';
import type { ComponentType, CSSProperties } from 'react';
import FlightTakeoffOutlinedIcon from '@mui/icons-material/FlightTakeoffOutlined';
import HotelOutlinedIcon from '@mui/icons-material/HotelOutlined';
import LocalActivityOutlinedIcon from '@mui/icons-material/LocalActivityOutlined';
import DirectionsCarFilledOutlinedIcon from '@mui/icons-material/DirectionsCarFilledOutlined';
import MusicNoteOutlinedIcon from '@mui/icons-material/MusicNoteOutlined';
import TheatersOutlinedIcon from '@mui/icons-material/TheatersOutlined';
import GroupsOutlinedIcon from '@mui/icons-material/GroupsOutlined';
import ShoppingBagOutlinedIcon from '@mui/icons-material/ShoppingBagOutlined';
import RestaurantOutlinedIcon from '@mui/icons-material/RestaurantOutlined';
import MapOutlinedIcon from '@mui/icons-material/MapOutlined';
import LocalParkingOutlinedIcon from '@mui/icons-material/LocalParkingOutlined';
import AltRouteOutlinedIcon from '@mui/icons-material/AltRouteOutlined';
import PublicOutlinedIcon from '@mui/icons-material/PublicOutlined';
import DirectionsBoatFilledOutlinedIcon from '@mui/icons-material/DirectionsBoatFilledOutlined';
import TrainOutlinedIcon from '@mui/icons-material/TrainOutlined';
import CommuteOutlinedIcon from '@mui/icons-material/CommuteOutlined';

import { ActivityType } from '../../../../types/activity.types';
import { TripStop } from '../../../../types/trip.types';
import { generateActiveGradient } from '../../../../utils/gradientUtils';

export type DayStatus = 'completed' | 'active' | 'upcoming';

type TimelineIconProps = {
  size?: number;
  gradient?: {
    id: string;
    stops: { offset: string; color: string }[];
  };
};

const createTimelineIcon = (MuiIcon: ComponentType<any>) => {
  const TimelineIcon = ({ size = 40, gradient }: TimelineIconProps) => {
    const gradientColors = gradient?.stops?.map(stop => stop.color) ?? [];
    const background =
      gradientColors.length > 1
        ? `linear-gradient(135deg, ${gradientColors.join(', ')})`
        : '#DEE3EB';

    const wrapperStyle: CSSProperties = {
      width: size,
      height: size,
      borderRadius: '50%',
      background,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      flexShrink: 0,
    };

    return createElement(
      'div',
      { style: wrapperStyle },
      createElement(MuiIcon, {
        htmlColor: '#FFFFFF',
        sx: {
          fontSize: size * 0.48,
          color: '#FFFFFF !important',
          fill: '#FFFFFF !important',
        },
      })
    );
  };

  return TimelineIcon;
};

const iconMap: Record<ActivityType, ComponentType<TimelineIconProps>> = {
  Flight: createTimelineIcon(FlightTakeoffOutlinedIcon),
  Hotel: createTimelineIcon(HotelOutlinedIcon),
  Activity: createTimelineIcon(LocalActivityOutlinedIcon),
  CarRental: createTimelineIcon(DirectionsCarFilledOutlinedIcon),
  Concert: createTimelineIcon(MusicNoteOutlinedIcon),
  Theatre: createTimelineIcon(TheatersOutlinedIcon),
  Meeting: createTimelineIcon(GroupsOutlinedIcon),
  Shopping: createTimelineIcon(ShoppingBagOutlinedIcon),
  Restaurant: createTimelineIcon(RestaurantOutlinedIcon),
  Tour: createTimelineIcon(MapOutlinedIcon),
  Parking: createTimelineIcon(LocalParkingOutlinedIcon),
  Directions: createTimelineIcon(AltRouteOutlinedIcon),
  BorderCrossing: createTimelineIcon(PublicOutlinedIcon),
  Ferry: createTimelineIcon(DirectionsBoatFilledOutlinedIcon),
  Cruise: createTimelineIcon(DirectionsBoatFilledOutlinedIcon),
  Rail: createTimelineIcon(TrainOutlinedIcon),
  Transportation: createTimelineIcon(CommuteOutlinedIcon),
};

export const getActivityIcon = (activityType: ActivityType) => {
  return iconMap[activityType] || iconMap.Flight;
};

export const getActivityGradient = (activityId: number) => {
  return generateActiveGradient(`activity-${activityId}`, 'timeline');
};

export const isActivityActive = (activity: TripStop): boolean => {
  const now = new Date();

  let startTime: Date;
  let endTime: Date;

  switch (activity.stop_type) {
    case 'Flight':
      startTime = new Date(activity.departure_date_time);
      endTime = new Date(activity.arrival_date_time);
      break;

    case 'Hotel':
      startTime = new Date(activity.check_in_date_time);
      endTime = new Date(activity.check_out_date_time);
      break;

    case 'Ferry':
    case 'Cruise':
    case 'Rail':
    case 'Transportation':
      startTime = new Date(activity.departure_date_time);
      endTime = new Date(activity.arrival_date_time);
      break;

    case 'BorderCrossing':
      startTime = new Date(activity.start_date_time);
      endTime = new Date(startTime.getTime() + 60 * 60 * 1000);
      break;

    default:
      startTime = new Date(activity.start_date_time);
      endTime = new Date(activity.end_date_time);
      break;
  }

  return now >= startTime && now <= endTime;
};

export const getActivityStatus = (activity: TripStop): 'upcoming' | 'active' | 'completed' => {
  const now = new Date();

  if (isActivityActive(activity)) {
    return 'active';
  }

  let startTime: Date;

  switch (activity.stop_type) {
    case 'Flight':
      startTime = new Date(activity.departure_date_time);
      break;

    case 'Hotel':
      startTime = new Date(activity.check_in_date_time);
      break;

    case 'Ferry':
    case 'Cruise':
    case 'Rail':
    case 'Transportation':
      startTime = new Date(activity.departure_date_time);
      break;

    case 'BorderCrossing':
      startTime = new Date(activity.start_date_time);
      break;

    default:
      startTime = new Date(activity.start_date_time);
      break;
  }

  return now < startTime ? 'upcoming' : 'completed';
};

export const getDayStatus = (activities: TripStop[]): DayStatus => {
  if (!activities || activities.length === 0) return 'upcoming';

  const now = new Date();
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());

  const getActivityDate = (activity: TripStop): Date => {
    let dateString: string;

    switch (activity.stop_type) {
      case 'Flight':
        dateString = activity.departure_date_time;
        break;
      case 'Hotel':
        dateString = activity.check_in_date_time;
        break;
      case 'Ferry':
      case 'Cruise':
      case 'Rail':
      case 'Transportation':
        dateString = activity.departure_date_time;
        break;
      case 'BorderCrossing':
        dateString = activity.start_date_time;
        break;
      default:
        dateString = activity.start_date_time;
        break;
    }

    return new Date(dateString);
  };

  const dayDate = getActivityDate(activities[0]);
  const dayOnly = new Date(dayDate.getFullYear(), dayDate.getMonth(), dayDate.getDate());

  if (dayOnly < today) return 'completed';
  if (dayOnly.getTime() === today.getTime()) return 'active';
  return 'upcoming';
};
